/**
 * @fileoverview Individual Notification Item Component
 *
 * Displays a single notification with icon, message, and actions.
 */

import { useNotificationCenter } from '../context/NotificationCenterContext';
import { useNavigate } from 'react-router-dom';
import {
  FiInfo,
  FiAlertTriangle,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiClock,
  FiDollarSign,
  FiUsers,
  FiFileText,
  FiSettings
} from 'react-icons/fi';
import '../styles/NotificationItem.css';

const NotificationItem = ({ notification }) => {
  const { markAsRead, dismissNotification } = useNotificationCenter();
  const navigate = useNavigate();

  // Get icon based on type and category
  const getIcon = () => {
    // First check type
    switch (notification.type) {
      case 'success':
        return <FiCheckCircle className="notification-icon notification-icon-success" />;
      case 'warning':
        return <FiAlertTriangle className="notification-icon notification-icon-warning" />;
      case 'error':
        return <FiAlertCircle className="notification-icon notification-icon-error" />;
      default:
        // Then check category
        switch (notification.category) {
          case 'time_bank':
            return <FiClock className="notification-icon notification-icon-info" />;
          case 'invoice':
            return <FiDollarSign className="notification-icon notification-icon-info" />;
          case 'client':
            return <FiUsers className="notification-icon notification-icon-info" />;
          case 'report':
            return <FiFileText className="notification-icon notification-icon-info" />;
          case 'system':
            return <FiSettings className="notification-icon notification-icon-info" />;
          default:
            return <FiInfo className="notification-icon notification-icon-info" />;
        }
    }
  };

  // Format time ago
  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Handle notification click
  const handleClick = () => {
    // Mark as read
    if (!notification.is_read) {
      markAsRead(notification.id);
    }

    // Navigate to action URL if provided
    if (notification.action_url) {
      navigate(notification.action_url);
    }
  };

  // Handle dismiss
  const handleDismiss = (e) => {
    e.stopPropagation();
    dismissNotification(notification.id);
  };

  return (
    <div
      className={`notification-item ${!notification.is_read ? 'notification-item-unread' : ''} ${notification.action_url ? 'notification-item-clickable' : ''}`}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className="notification-item-icon">
        {getIcon()}
      </div>

      {/* Content */}
      <div className="notification-item-content">
        {notification.title && (
          <div className="notification-item-title">
            {notification.title}
            {!notification.is_read && (
              <span className="notification-unread-dot" />
            )}
          </div>
        )}
        <div className="notification-item-message">
          {notification.message}
        </div>
        <div className="notification-item-meta">
          <span className="notification-item-time">
            {getTimeAgo(notification.created_at)}
          </span>
          {notification.action_text && notification.action_url && (
            <>
              <span className="notification-meta-separator">•</span>
              <span className="notification-item-action-text">
                {notification.action_text}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Dismiss button */}
      <button
        className="notification-item-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss notification"
      >
        <FiX size={16} />
      </button>
    </div>
  );
};

export default NotificationItem;
